import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { LuBuilding2, LuCopy, LuRefreshCw, LuChevronDown, LuChevronRight, LuPencil, LuTrash2, LuPlus, LuSearch, LuX } from "react-icons/lu";
import OrganisationService from "../../services/api/organisation";
import SAPagination from "../../components/common/SAPagination";
import SATooltip from "../../components/common/SATooltip";
import { API_RESPONSE } from "../../settings/shared/constants";

const PAGE_SIZE = 10;
// Page size used to fetch every account once when the user starts searching.
// If your server caps page size (e.g. 100), lower this to the maximum allowed.
const SEARCH_FETCH_SIZE = 1000;

const T = {
  border: "rgba(0,0,0,0.07)",
  borderMid: "rgba(0,0,0,0.12)",
  surface: "#ffffff",
  textPrimary: "#0f172a",
  textSecondary: "#64748b",
  textMuted: "#94a3b8",
  accent: "#3b82f6",
  fontSans: "'DM Sans','Segoe UI',sans-serif",
  fontMono: "'DM Mono','Courier New',monospace",
};

const GRID = "32px minmax(180px,1.5fr) minmax(170px,1.4fr) minmax(130px,1fr) minmax(110px,0.9fr) minmax(130px,1fr) 96px";

/* ───────── helpers ───────── */
const has = (v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
const fmtDate = (d) => (d ? moment.utc(d).local().format("MMM DD, YYYY · hh:mm A") : null);
const fmtShort = (d) => (d ? moment.utc(d).local().format("MMM DD, YYYY") : "—");

const ACCOUNT_TYPE = {
  TENANT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  PARTNER: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
};
const TENANT_TYPE = {
  PAID: { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" },
  POC: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
};
const TRIAL_STATUS = {
  ACTIVE: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  EXPIRED: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};
const NEUTRAL = { bg: "#f1f5f9", color: "#64748b", border: "rgba(0,0,0,0.06)" };

function Chip({ label, tone = NEUTRAL, dot }) {
  return (
    <span
      className="ac-chip"
      style={{ background: tone.bg, color: tone.color, borderColor: tone.border }}
    >
      {dot ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot, marginRight: 5 }} /> : null}
      {label}
    </span>
  );
}

function Avatar({ name }) {
  const initial = String(name || "A").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: 40, height: 40, borderRadius: 11, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(145deg,#eff6ff 0%,#f8fafc 100%)",
        color: T.accent, border: `1px solid ${T.border}`,
        fontSize: 14, fontWeight: 700,
      }}
    >
      {initial}
    </div>
  );
}

function CreditsBar({ total, remaining }) {
  if (!has(total) || !has(remaining) || Number(total) <= 0) {
    return <span style={{ fontSize: 12, color: T.textMuted }}>—</span>;
  }
  const used = Math.max(0, Number(total) - Number(remaining));
  const pct = Math.min(100, (used / Number(total)) * 100);
  const color = pct >= 85 ? "#dc2626" : pct >= 60 ? "#d97706" : "#3b82f6";
  return (
    <div style={{ minWidth: 0, maxWidth: 220 }}>
      <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4, whiteSpace: "nowrap" }}>
        <strong style={{ color: T.textPrimary }}>{used}</strong> / {total} used
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "#e2e8f0", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width .3s" }} />
      </div>
    </div>
  );
}

function DetailField({ label, value, mono, copy }) {
  return (
    <div className="ac-detail">
      <div className="ac-detail-label">{label}</div>
      <div className="ac-detail-value" style={{ fontFamily: mono ? T.fontMono : T.fontSans }} title={String(value)}>
        <span style={{ minWidth: 0, wordBreak: "break-all" }}>{String(value)}</span>
        {copy ? (
          <SATooltip title="Copy">
            <button
              type="button"
              className="ac-copy"
              onClick={() => {
                navigator.clipboard?.writeText(String(value));
                toast.success(`${label} copied`);
              }}
            >
              <LuCopy size={12} />
            </button>
          </SATooltip>
        ) : null}
      </div>
    </div>
  );
}

function Section({ title, items }) {
  const visible = items.filter(([, value]) => has(value));
  if (visible.length === 0) return null;

  const headingColors = {
    "Trial details": "#2563eb",
    "Organisation details": "#059669",
    "System details": "#7c3aed",
  };

  const headingColor = headingColors[title] || T.textPrimary;

  return (
    <div className="ac-section">
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: headingColor,
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: `1px solid ${headingColor}30`,
        }}
      >
        {title}
      </div>
      <div className="ac-grid">
        {visible.map(([label, value, opts]) => (
          <DetailField key={label} label={label} value={value} {...(opts || {})} />
        ))}
      </div>
    </div>
  );
}

function ExpandPanel({ item }) {
  const used = has(item.totalTrialCredits) && has(item.remainingTrialCredits)
    ? Number(item.totalTrialCredits) - Number(item.remainingTrialCredits)
    : null;

  return (
    <div className="ac-expand" onClick={(e) => e.stopPropagation()}>
      <Section
        title="Trial details"
        items={[
          ["Total credits", item.totalTrialCredits],
          ["Remaining credits", item.remainingTrialCredits],
          ["Used credits", used],
          ["Duration (days)", item.trialDurationInDays],
          ["Trial start", fmtDate(item.trialStartDate)],
          ["Trial end", fmtDate(item.trialEndDate)],
          ["Max runtime", item.maxRuntime],
          ["Agent executions", item.agentExecutionCount],
          ["Max agent executions", item.maxAgentExecutions],
        ]}
      />
      <Section
        title="Organisation details"
        items={[
          ["Description", item.description],
          ["URL", item.url],
          ["Office address", item.officeAddress],
          ["City", item.city],
          ["Branches", Array.isArray(item.branches) ? item.branches.join(", ") : item.branches],
          ["Phone", item.phoneNumber],
          ["Hours", item.hours > 0 ? item.hours : null],
          ["Environment", item.environmentType],
          ["Image", item.imageName],
          ["Version", item.version],
        ]}
      />
      <Section
        title="System details"
        items={[
          ["name", `${item.firstName || ""} ${item.lastName || ""}`.trim()],
          // ["Identifier", item.identifier, { mono: true, copy: true }],
          ["Tenant identifier", item.tenantIdentifier, { mono: true, copy: true }],
          // ["Record ID", item.id, { mono: true, copy: true }],
          ["Created by", item.createdBy],
          ["Created", fmtDate(item.createdDate)],
          ["Updated by", item.updatedBy],
          ["Updated", fmtDate(item.updatedDate)],
        ]}
      />
    </div>
  );
}

function Skeleton({ rows = 5 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="ac-row ac-row--skel" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 }}>
          <span className="ac-skel" style={{ width: 28, height: 28 }} />
          <span className="ac-skel" style={{ height: 14, width: "70%" }} />
          <span className="ac-skel" style={{ height: 12, width: "75%" }} />
          <span className="ac-skel" style={{ height: 22, width: 70 }} />
          <span className="ac-skel" style={{ height: 22, width: 60 }} />
          <span className="ac-skel" style={{ height: 8, width: "80%" }} />
          <span className="ac-skel" style={{ height: 12, width: 80 }} />
          <span className="ac-skel" style={{ height: 28, width: 70, justifySelf: "end" }} />
        </div>
      ))}
    </div>
  );
}

/* ───────── page ───────── */
const AccountsList = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const requestRef = useRef(0);

  // search
  const [search, setSearch] = useState("");
  const [allItems, setAllItems] = useState(null); // every account, fetched once when searching starts
  const searching = search.trim() !== "";

  const load = useCallback((pageNumber) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    OrganisationService.getAllOrganisations(pageNumber, PAGE_SIZE)
      .then((response) => {
        if (requestId !== requestRef.current) return; // ignore stale responses
        if (response?.status === API_RESPONSE.SUCCESS) {
          const data = response.organisationDTOPage || {};
          setItems(data.content || []);
          setTotalPages(data.totalPages || 0);
          setTotalItems(data.totalElements || 0);
        } else {
          toast.error(response?.errorMessage || "Failed to load accounts");
        }
      })
      .catch((err) => {
        if (requestId !== requestRef.current) return;
        toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to load accounts");
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, []);

  // Normal paged loading (skipped while searching)
  useEffect(() => {
    if (searching) return;
    load(page);
  }, [page, load, searching]);

  // Fetch all accounts once the first time the user searches
  useEffect(() => {
    if (!searching || allItems) return;
    let cancelled = false;
    OrganisationService.getAllOrganisations(0, SEARCH_FETCH_SIZE)
      .then((response) => {
        if (cancelled) return;
        if (response?.status === API_RESPONSE.SUCCESS) {
          setAllItems(response.organisationDTOPage?.content || []);
        } else {
          toast.error(response?.errorMessage || "Failed to search accounts");
          setAllItems([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.errorMessage || err?.message || "Failed to search accounts");
        setAllItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [searching, allItems]);

  // Filter in the browser (name or email)
  const matches = useMemo(() => {
    if (!searching || !allItems) return null;
    const q = search.trim().toLowerCase();
    return allItems.filter((o) =>
      [o.name, o.email].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [search, searching, allItems]);

  // What to show: search results (paginated locally) or the normal server page
  const isLoading = searching ? !allItems : loading;
  const visibleItems = matches
    ? matches.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : items;
  const shownTotal = matches ? matches.length : totalItems;
  const shownPages = matches ? Math.ceil(matches.length / PAGE_SIZE) : totalPages;

  const handlePageChange = (next) => {
    setExpandedId(null);
    setPage(next);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(0);
    setExpandedId(null);
  };

  const refresh = () => {
    setExpandedId(null);
    if (searching) {
      setAllItems(null); // triggers a fresh fetch of all accounts
    } else {
      load(page);
    }
  };

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <>
      <style>{`
        @keyframes ac-spin { to { transform: rotate(360deg); } }
        @keyframes ac-shimmer { 0%{opacity:.45} 50%{opacity:.95} 100%{opacity:.45} }
        .ac-page { font-family: ${T.fontSans}; padding: 20px 24px 24px; height: 100%; overflow-y: auto; box-sizing: border-box; }
        .ac-card { background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 12px; overflow: hidden;
          transition: border-color .15s, box-shadow .15s, transform .15s; }
        .ac-card:hover { border-color: rgba(59,130,246,.28); box-shadow: 0 6px 18px rgba(15,23,42,.06); transform: translateY(-1px); }
        .ac-card.expanded { background: #fafbfc; border-color: rgba(59,130,246,.22); }
        .ac-row { display: grid; grid-template-columns: ${GRID}; gap: 0 12px; align-items: center; padding: 9px; cursor: pointer; }
        .ac-row--skel { cursor: default; pointer-events: none; }
        .ac-head { display: grid; grid-template-columns: ${GRID}; gap: 0 12px; padding: 0 16px 6px; font-size: 11px; font-weight: 600;
          color: ${T.textMuted}; text-transform: uppercase; letter-spacing: .04em; }
        .ac-chip { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 8px; font-size: 11px; font-weight: 500;
          white-space: nowrap; border: 1px solid transparent; }
        .ac-expand-btn { width: 28px; height: 28px; padding: 0; border: none; background: transparent; color: ${T.textSecondary};
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; }
        .ac-expand-btn:hover { background: #f1f5f9; color: ${T.textPrimary}; }
        .ac-expand { padding: 14px 16px 16px; background: #f8fafc; border-top: 1px solid rgba(0,0,0,.06);
          display: flex; flex-direction: column; gap: 10px; }
        .ac-section { background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 10px; padding: 12px 14px; }
        .ac-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; }
        .ac-detail { background: #f8fafc; border: .5px solid ${T.border}; border-radius: 10px; padding: 10px 12px; min-height: 56px; }
        .ac-detail-label { font-size: 10px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; color: ${T.textMuted}; margin-bottom: 5px; }
        .ac-detail-value { font-size: 13px; font-weight: 500; color: ${T.textPrimary}; line-height: 1.4; display: flex; align-items: flex-start; gap: 6px; }
        .ac-copy { border: none; background: transparent; color: ${T.textMuted}; cursor: pointer; padding: 2px; border-radius: 5px; flex-shrink: 0; }
        .ac-copy:hover { background: #e2e8f0; color: ${T.textPrimary}; }
        .ac-refresh:hover:not(:disabled) { background: #eff6ff !important; color: #1d4ed8 !important; }
        .ac-skel { display: block; border-radius: 6px; background: linear-gradient(90deg,#eef2f7 0%,#e2e8f0 50%,#eef2f7 100%);
          background-size: 200% 100%; animation: ac-shimmer 1.5s ease-in-out infinite; }
        .ac-search { position: relative; flex: 1 1 240px; max-width: 360px; min-width: 200px; margin: 0 auto; }
        .ac-search input { width: 100%; height: 36px; box-sizing: border-box; padding: 0 32px 0 32px;
          border: 0.5px solid ${T.borderMid}; border-radius: 8px; background: ${T.surface};
          font-family: ${T.fontSans}; font-size: 13px; color: ${T.textPrimary}; outline: none;
          transition: border-color .15s, box-shadow .15s; }
        .ac-search input:focus { border-color: ${T.accent}; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
        .ac-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: ${T.textMuted}; pointer-events: none; }
        .ac-search-clear { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 22px; height: 22px;
          border: none; background: transparent; color: ${T.textMuted}; border-radius: 6px; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; }
        .ac-search-clear:hover { background: #f1f5f9; color: ${T.textPrimary}; }
        .ac-pagination .page-footer { margin-top: 0 !important; padding: 4px 0 !important; }
        .ac-pagination .pagination-info { font-size: 11px !important; }
        .ac-pagination .go-to-page { margin-top: 4px !important; }
        .ac-pagination .go-to-page label { font-size: 11px; color: ${T.textSecondary}; white-space: nowrap; }
        .ac-pagination .go-to-page .form-control { height: 26px; font-size: 12px; padding: 2px 6px; }
        .ac-pagination .go-to-page .btn { height: 26px; padding: 0 8px; font-size: 11px; line-height: 1;
          background: #3b82f6 !important; border-color: #3b82f6 !important; color: #fff !important; }
        .ac-pagination .pagination { gap: 2px; }
        .ac-pagination .pagination .page-link { padding: 2px 8px !important; margin: 0 !important; font-size: 12px; min-width: 28px; height: 28px;
          display: inline-flex; align-items: center; justify-content: center; border-radius: 6px !important; }
        @media (max-width: 1100px) { .ac-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
        @media (max-width: 900px) {
          .ac-head { display: none; }
          .ac-row { grid-template-columns: 36px 1fr; gap: 10px; }
          .ac-row > :nth-child(n+3) { grid-column: 2; }
        }
        @media (max-width: 640px) { .ac-grid { grid-template-columns: 1fr; } }
      `}</style>

      <div className="ac-page">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "#172033", lineHeight: 1.2 }}>Accounts</div>
            <div style={{ fontSize: 15, color: "#667085", marginTop: 6 }}>All tenant and partner accounts on the platform</div>
          </div>

          {/* Search */}
          <div className="ac-search">
            <LuSearch size={14} className="ac-search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name or email…"
              aria-label="Search accounts"
            />
            {search && (
              <button type="button" className="ac-search-clear" aria-label="Clear search" onClick={() => handleSearchChange("")}>
                <LuX size={13} />
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              disabled
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "var(--activemenu, #3d91e4)", color: "#fff", border: "none",
                borderRadius: 8, padding: "0 16px", height: 36,
                fontFamily: T.fontSans, fontSize: 13, fontWeight: 500,
                cursor: "not-allowed", opacity: 0.5,
              }}
            >
              <LuPlus size={13} /> Create account
            </button>

            <SATooltip title="Refresh accounts">
              <button
                type="button"
                className="ac-refresh"
                onClick={refresh}
                disabled={isLoading}
                style={{
                  width: 34, height: 34, border: `0.5px solid ${T.borderMid}`, borderRadius: 8, background: T.surface,
                  cursor: isLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  color: T.textSecondary, opacity: isLoading ? 0.7 : 1,
                }}
              >
                <span style={{ display: "inline-flex", animation: isLoading ? "ac-spin .8s linear infinite" : "none" }}>
                  <LuRefreshCw size={13} />
                </span>
              </button>
            </SATooltip>
          </div>
        </div>

        <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>
          {isLoading && visibleItems.length === 0
            ? (searching ? "Searching accounts…" : "Loading accounts…")
            : `${shownTotal} ${shownTotal === 1 ? "account" : "accounts"}${searching ? ` matching "${search.trim()}"` : ""}`}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(isLoading || visibleItems.length > 0) && (
            <div className="ac-head">
              <span />
              <span>Account</span>
              <span>Email</span>
              <span>Type</span>
              <span>Status</span>
              <span>Credits</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>
          )}

          {isLoading ? (
            <Skeleton rows={Math.min(PAGE_SIZE, 6)} />
          ) : visibleItems.length > 0 ? (
            visibleItems.map((org, idx) => {
              const id = org.id || org.identifier || idx;
              const expanded = expandedId === id;
              const isActive = org.active !== false;

              return (
                <div key={id} className={`ac-card${expanded ? " expanded" : ""}`}>
                  <div
                    className="ac-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(id);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className="ac-expand-btn"
                      aria-expanded={expanded}
                      aria-label={expanded ? "Collapse account details" : "Expand account details"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(id);
                      }}
                    >
                      {expanded ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
                    </button>

                    {/* Account */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <Avatar name={org.name} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {org.name || "—"}
                        </div>
                      </div>
                    </div>

                    {/* Email */}
                    <div style={{ minWidth: 0, fontSize: 13, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={org.email}>
                      {org.email || "—"}
                    </div>

                    {/* Type */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {org.tenantType ? (
                        <Chip label={org.tenantType} tone={TENANT_TYPE[org.tenantType] || NEUTRAL} />
                      ) : org.accountType && org.accountType !== "TENANT" ? (
                        <Chip label={org.accountType} tone={ACCOUNT_TYPE[org.accountType] || NEUTRAL} />
                      ) : (
                        <span style={{ fontSize: 12, color: T.textMuted }}>—</span>
                      )}
                    </div>
                    {/* Status */}
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        tone={isActive ? TRIAL_STATUS.ACTIVE : NEUTRAL}
                        dot={isActive ? "#10b981" : "#94a3b8"}
                      />
                    </div>

                    {/* Credits */}
                    <CreditsBar total={org.totalTrialCredits} remaining={org.remainingTrialCredits} />

                    {/* Actions */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }} onClick={(e) => e.stopPropagation()}>
                      <SATooltip title="Edit">
                        <button
                          type="button"
                          disabled
                          style={{ width: 32, height: 32, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, cursor: "not-allowed", display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.textMuted, opacity: 0.4, boxSizing: "border-box" }}
                        >
                          <LuPencil size={14} />
                        </button>
                      </SATooltip>
                      <SATooltip title="Delete">
                        <button
                          type="button"
                          disabled
                          style={{ width: 32, height: 32, padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, cursor: "not-allowed", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#dc2626", opacity: 0.4, boxSizing: "border-box" }}
                        >
                          <LuTrash2 size={14} />
                        </button>
                      </SATooltip>
                    </div>
                  </div>

                  {expanded && <ExpandPanel item={org} />}
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: "center", padding: "56px 24px", background: "linear-gradient(180deg,#fff 0%,#f8fafc 100%)", borderRadius: 14, border: `1px dashed ${T.borderMid}` }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", background: "#eff6ff", color: T.accent }}>
                <LuBuilding2 size={20} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>No accounts found</div>
              <p style={{ fontSize: 13, color: T.textSecondary, margin: 0 }}>
                {searching ? "Try a different name or email." : "Accounts will appear here once they are created."}
              </p>
            </div>
          )}

          {!isLoading && shownPages > 1 ? (
            <div className="ac-pagination" style={{ flexShrink: 0, paddingTop: 4 }}>
              <SAPagination
                currentPage={page}
                totalPages={shownPages}
                totalItems={shownTotal}
                itemsPerPage={PAGE_SIZE}
                onPageChange={handlePageChange}
                showGoToPage
                size="sm"
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default AccountsList;